import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGeneralExams } from '../lib/api';
import { PageTransition, Stagger, Pressable, Skeleton } from '../lib/motion';

// Per-exam brand accents, assigned deterministically by sorted position so an
// exam keeps its accent between visits. Purely decorative.
const ACCENTS = [
  { bar: 'from-teal-400 to-emerald-500', text: 'text-teal-600' },
  { bar: 'from-indigo-400 to-purple-500', text: 'text-indigo-600' },
  { bar: 'from-amber-400 to-orange-500', text: 'text-amber-600' },
  { bar: 'from-sky-400 to-blue-500', text: 'text-blue-600' },
  { bar: 'from-rose-400 to-pink-500', text: 'text-red-600' },
  { bar: 'from-violet-400 to-fuchsia-500', text: 'text-violet-600' },
] as const;

export function ExamsPage() {
  const navigate = useNavigate();
  const { data: exams, isLoading } = useGeneralExams();

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const sorted = useMemo(
    () => [...(exams?.exams ?? [])].sort((a, b) => a.code.localeCompare(b.code)),
    [exams?.exams],
  );

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto px-4 pb-24 pt-1">
        <Skeleton className="h-8 w-36" />
        <Skeleton className="h-4 w-64 mt-2" />
        <div className="grid grid-cols-2 gap-3 mt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-3xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <PageTransition className="max-w-lg mx-auto px-4 pb-24 pt-1">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-squishy-dark tracking-tight">Exams</h1>
        <p className="text-sm text-slate-500 mt-1">Pick your exam. We'll take it from here.</p>
      </header>

      <Stagger className="grid grid-cols-2 gap-3">
        {sorted.map((e, i) => {
          const accent = ACCENTS[i % ACCENTS.length]!;
          return (
            <Stagger.Item key={e.id} className="h-full">
              <Pressable
                onClick={() => navigate(`/exam/${e.id}`)}
                aria-label={`Open ${e.code}: ${e.name}`}
                className="w-full h-full min-h-[8rem] text-left bg-white border border-slate-100 rounded-3xl p-5 shadow-card hover:shadow-pop transition-shadow flex flex-col"
              >
                <span
                  className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${accent.bar} flex items-center justify-center font-display text-lg font-semibold text-white shadow-soft`}
                  aria-hidden
                >
                  {e.code[0]}
                </span>
                <span className="font-display text-lg font-semibold text-squishy-dark leading-tight mt-3 block">
                  {e.code}
                </span>
                <span className="text-xs text-slate-500 leading-snug mt-1 line-clamp-2 block">
                  {e.name}
                </span>
                <span className={`mt-auto pt-3 inline-flex items-center gap-1 text-[11px] font-semibold ${accent.text}`}>
                  Open
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3" aria-hidden>
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </span>
              </Pressable>
            </Stagger.Item>
          );
        })}
      </Stagger>

      {sorted.length === 0 && (
        <div className="text-center py-16">
          <p className="text-sm font-medium text-slate-500">No exams available yet.</p>
          <p className="text-xs text-slate-400 mt-1">Check back soon.</p>
        </div>
      )}
    </PageTransition>
  );
}
